import { useEffect, useRef, useState, type ReactNode } from "react";
import type { SettlementRecord, Worker } from "../types";
import type { SettlementSort } from "./usePayrollData";
import { calculationQuantity, calculationUnit, money, sourceLabel, statusLabel } from "../payrollPresentation";
import { settlementRecordBankReady } from "../settlementBankAccount";
import { Button } from "./ui/Button";
import { Checkbox } from "./ui/Checkbox";
/* 이 표는 머리글을 직접 그린다(ColumnFilterHead를 쓰지 않는다). 그 CSS는 컴포넌트가
   들고 있었으므로 여기서 따로 불러와야 목록이 스타일도, 붙을 자리도 갖는다. */
import "./ColumnFilterHead.css";
import { EmptyState, TableFrame } from "./ui/Surface";
import { usePlanningBaseline } from "./ui/DesignSystem";
import { PlanningPayrollRecords } from "./PlanningPayrollRecords";
import type { DataTableProps } from "./easyui/AdvancedDataTable";

/* 하위작업 칸에는 하위작업만 적는다 — 공제·추가 지급은 공정이 아니라 금액의 종류라,
   여기에 "공제 · 공제"처럼 적히면 그 줄이 어느 공정의 일인지를 묻는 칸이 종류를 답한다.
   무엇으로 잡힌 줄인지는 옆의 "정산 방식" 칸이 말한다. */
export function PayrollRecordTitle({ record, withProject = true }: { record: SettlementRecord; withProject?: boolean }) {
  return <span className="payroll-record-title"><strong>{record.subtaskName ?? "-"}</strong>{withProject && record.projectName && <small>{record.projectName}</small>}</span>;
}

/* 한 칸이 그 줄의 내력을 다 말한다 — 기계가 잡았는지 사람이 적었는지, 사람이 적었으면
   무엇으로 적었는지. 전에는 이것이 두 열("구분"과 "정산 방식")로 갈려 있었는데, 구분은
   돈의 성격(도급·시급·계약)을, 방식은 자동·수동을 말해서 같은 줄을 두 축으로 쪼개 읽어야
   했다. 더구나 자동은 늘 도급이라 구분 칸에 자동과 수동이 같이 들어와, 그 칸만 보고는
   아무것도 가릴 수 없었다. */
/* 「자동」은 불출·회수에서 저절로 생긴 줄(도급 원천 piecework 의 기본 줄)뿐이다. 수동 정산 창으로 적은 월급·도급(contract)·
   시급(part_time)도 기본 줄(base)로 저장되므로 recordKind 만 보면 「자동」으로 잘못 읽혔다(2026-10-19 밤 지윤). */
export const settlementEntryLabel = (record: SettlementRecord) =>
  record.recordKind === "base" && record.source === "piecework" ? "자동"
    : record.adjustmentKind === "deduction" ? "수동 · 공제"
    : record.adjustmentKind === "addition" ? "수동 · 추가 지급"
    : record.source === "part_time" ? "수동 · 시급"
    /* 도급과 월급은 저장될 때 같은 칸(contract)에 들어간다 — 가르는 것은 수량이다.
       개수로 받으면 수량이 남고, 달로 받으면 셀 개수가 없다. */
    : record.quantity !== null && record.quantity > 1 ? "수동 · 도급"
    : "수동 · 월급";

export function PayrollRecordAmount({ record }: { record: SettlementRecord }) {
  if (record.recordKind === "manual") return <span className="payroll-record-amount"><span className="payroll-amount">{(record.manualAmount ?? 0) > 0 ? "+" : ""}{money(record.manualAmount ?? 0)}</span><span className="payroll-pretax-amount">(세후 조정)</span></span>;
  // 별도 조정 행은 세전 증감액 자체를 나타내므로 기존 부호와 금액을 유지한다.
  const adjustment = record.adjustmentKind === "deduction" ? `-${money(record.adjustmentAmount)}`
    : record.adjustmentKind === "addition" ? `+${money(record.adjustmentAmount)}` : null;
  return <span className="payroll-record-amount">
    <span className="payroll-amount">{adjustment ?? money(record.payableAmount)}</span>
    <span className="payroll-pretax-amount">({adjustment ?? money(record.taxableGrossAmount)})</span>
  </span>;
}

const workTimeFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "Asia/Seoul", hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", second: "2-digit",
});
const workMoment = (value?: string | null): { date: string; time: string | null } | null => {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return { date: value, time: null };
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const parts = workTimeFormatter.formatToParts(parsed);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find(item => item.type === type)?.value ?? "";
  return { date: `${part("year")}-${part("month")}-${part("day")}`, time: `${part("hour")}:${part("minute")}:${part("second")}` };
};

export function PayrollRecordWorkTime({ record }: { record: SettlementRecord }) {
  if (record.recordKind === "manual") {
    const input = workMoment(record.createdAt);
    const time = input?.time === "12:00:00" ? null : input?.time;
    return <span className="payroll-record-work-time"><span>{input?.date ?? record.settlementDate}</span>{time && <small>{time}</small>}</span>;
  }
  const start = workMoment(record.workStartedAt);
  const end = workMoment(record.workCompletedAt);
  const dates = start && end ? start.date === end.date ? start.date : `${start.date} ~ ${end.date}` : start?.date ?? end?.date ?? record.settlementDate;
  const startTime = start?.time === "12:00:00" ? null : start?.time;
  const endTime = end?.time === "12:00:00" ? null : end?.time;
  const times = startTime && endTime && startTime !== endTime ? `${startTime} ~ ${endTime}` : startTime ?? endTime;
  const hideNoon = start?.time === "12:00:00" || end?.time === "12:00:00";
  return <span className="payroll-record-work-time"><span>{dates}</span>{times ? <small>{times}</small> : !hideNoon && <small>시각 미기록</small>}</span>;
}

type SelectionProps = { workers: Worker[]; selectedIds: string[]; onSelect: (ids: string[]) => void; onEdit?: (record: SettlementRecord) => void;
  onSelectAll?: (checked: boolean) => void; allSelected?: boolean };

/** 한 열이 거는 조건 하나. 고를 수 있는 값이 손에 꼽히면 목록, 계속 늘어나면 검색이다. */
export type ColumnFilterKey = "period" | "workerId" | "source" | "status" | "projectName" | "subtaskName";
export type ColumnFilterOption = { value: string; label: string };
export type ColumnFilters = {
  values: Partial<Record<ColumnFilterKey, string>>;
  options: Partial<Record<ColumnFilterKey, ColumnFilterOption[]>>;
  onChange: (key: ColumnFilterKey, value: string | undefined) => void;
  /* 기간은 값 목록으로 고를 수 없다 — 달 하나이거나, 시작일과 종료일 두 칸이다. 그 컨트롤을
     페이지가 만들어 주고 머리글은 자리만 내어 준다. 옆에 지금 고른 기간의 이름을 적는다:
     여기에는 "안 걸린 상태"가 없어서 ✕로 지울 수 없고, 무엇을 보는 중인지는 늘 보여야 한다. */
  period?: { label: string; control: ReactNode; active: boolean; onClear: () => void };
};

/**
 * 정산 원장 하나. 미정산만 따로 보여 주던 "정산 준비" 표는 같은 질의를 status=pending으로
 * 거른 것이었고, 그쪽의 "이체액"은 여기 "정산액"과 같은 값이었다. 표를 하나로 두고, 고를 수
 * 있는 행은 상태가 미정산인 행으로 한정한다. 묶음 안의 기록은 체크 대신 제외 동작을 제공한다.
 */
type PayrollRecordsProps = { records: SettlementRecord[]; showSource: boolean; disabled: boolean; sort: SettlementSort; descending?: boolean; onSort: (value: SettlementSort) => void; onDelete: (record: SettlementRecord) => void; onRemoveFromBatch?: (record: SettlementRecord) => void; emptyText?: string; selection?: SelectionProps; filters?: ColumnFilters; toolbarLead?: ReactNode;
  remote?: NonNullable<DataTableProps<SettlementRecord>["remote"]> & { loading?: boolean; error?: string; onRetry: () => void } };

export function PayrollRecords(props: PayrollRecordsProps) {
  const baseline = usePlanningBaseline();
  return baseline ? <PlanningPayrollRecords {...props} /> : <ManagementPayrollRecords {...props} />;
}

function ManagementPayrollRecords({ records, showSource, disabled, sort, onSort, onDelete, onRemoveFromBatch, emptyText, selection, filters }: PayrollRecordsProps) {
  /* 거르는 일은 그 열의 머리글이 맡는다. 표 밖에 컨트롤을 두면 그것이 표를 거르는지 위
     합계를 거르는지 자리가 말해 주지 못했다. 열려 있는 칸은 한 번에 하나다 — 여럿을 동시에
     펼쳐 두면 머리글 줄이 다른 물건이 된다. */
  const [openKey, setOpenKey] = useState<ColumnFilterKey | null>(null), [query, setQuery] = useState("");
  const searchInput = useRef<HTMLInputElement>(null);
  useEffect(() => { if (openKey) searchInput.current?.focus(); }, [openKey]);
  /* 기간이 걸리면 패널을 닫는다. 값 목록은 고르는 순간 닫히는데 기간만 열린 채 남아, 고른
     뒤에도 아래 줄을 가리고 서 있었다. */
  const periodActive = filters?.period?.active;
  useEffect(() => { if (periodActive) setOpenKey(current => current === "period" ? null : current); }, [periodActive]);
  /* 바깥을 누르면 닫는다. 머리글에 붙은 목록이라 blur만으로는 목록 안을 누를 때도 닫힌다. */
  useEffect(() => {
    if (!openKey) return;
    const dismiss = (event: MouseEvent) => { if (!(event.target as HTMLElement).closest("th")) { setOpenKey(null); setQuery(""); } };
    document.addEventListener("mousedown", dismiss);
    return () => document.removeEventListener("mousedown", dismiss);
  }, [openKey]);
  const people = new Map((selection?.workers ?? []).map(worker => [worker.id, worker]));
  const selectable = records.filter(record => record.status === "pending");
  const chosenHere = selection ? selectable.filter(record => selection.selectedIds.includes(record.id)) : [];

  const close = () => { setOpenKey(null); setQuery(""); };
  const pick = (key: ColumnFilterKey, value: string | undefined) => { filters!.onChange(key, value); close(); };
  const chosenLabel = (key: ColumnFilterKey) => {
    const value = filters?.values[key];
    return value ? filters?.options[key]?.find(option => option.value === value)?.label ?? value : undefined;
  };
  /* 검색칸은 컨트롤의 종류가 아니라 목록의 사정이다. 몇 개뿐이면 눈으로 훑는 게 빠르고,
     길어지면 그때 돋보기가 목록 위에 선다 — 거는 장치는 어느 열이든 ▾ 하나로 같다. */
  const NEEDS_SEARCH = 6;

  /** 머리글 하나. 글씨는 정렬이나 이름 그대로 두고, 거르는 ▾만 그 옆에 붙는다. */
  const head = (label: string, options?: { sort?: "date" | "worker"; filter?: ColumnFilterKey; content?: ReactNode }) => {
    const key = options?.filter;
    const period = key === "period" ? filters?.period : undefined;
    const chosen = period ? undefined : key && chosenLabel(key);
    const open = !!key && openKey === key;
    const list = key ? filters?.options[key] ?? [] : [];
    const searchable = list.length > NEEDS_SEARCH;
    const matches = searchable && query.trim() ? list.filter(option => option.label.includes(query.trim())) : list;
    const ascending = sort === "date_asc" || sort === "work_time_asc";
    return <th scope="col" key={label} aria-sort={options?.sort ? (ascending ? "ascending" : "descending") : undefined}>
      <span className="payroll-head">
      {options?.sort
        ? <button type="button" className="payroll-sort" aria-label={`${label}: ${ascending ? "빠른순" : "늦은순"}`} onClick={() => onSort(ascending ? "work_time" : "work_time_asc")}>{label} <span aria-hidden="true">{ascending ? "↑" : "↓"}</span></button>
        : key && filters && !period ? null : <span className="payroll-head-label">{options?.content ?? label}</span>}
      {/* 다른 열과 같은 규칙이다 — 걸어 두면 그 값이 이름 옆에 서고 ✕로 푼다. 안 걸린
          상태("전체")는 아무것도 적지 않는다: 모든 줄이 보이는 것이 기본이라 그 사실을
          한 번 더 말할 필요가 없다. */}
      {period && (period.active
        ? <>
            <span className="payroll-head-period">{period.label}</span>
            <button type="button" className="payroll-worker-search-open" aria-label="기간 필터 해제" data-testid="clear-filter-period"
              onClick={() => period.onClear()}>✕</button>
          </>
        : <button type="button" className="payroll-worker-search-open" aria-expanded={openKey === "period"} aria-label="기간 고르기" data-testid="open-filter-period"
            onClick={() => setOpenKey(current => current === "period" ? null : "period")}>▾</button>)}
      {key && filters && !period && <>
        <button type="button" className="payroll-filter-trigger" data-active={!!chosen || undefined}
          aria-expanded={open} aria-label={`${label} 거르기`} title={chosen ? `${label}: ${chosen}` : undefined} data-testid={`open-filter-${key}`}
          onClick={() => { setQuery(""); setOpenKey(current => current === key ? null : key); }}>
          <span>{label}</span><svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="m3 6 5 5 5-5" /></svg>
        </button>
        {chosen && <button type="button" className="payroll-worker-search-open" aria-label={`${chosen} 필터 해제`} data-testid={`clear-filter-${key}`}
          onClick={() => pick(key, undefined)}>✕</button>}
      </>}
      {openKey === "period" && period && <div className="payroll-head-menu payroll-head-period-menu">{period.control}</div>}
      {open && !period && <div className="payroll-head-menu">
        {/* 검색칸은 목록 위에 선다. 컨트롤 자체를 검색창으로 바꾸면 머리글 폭이 변해 아래 열이
            밀리고, 어느 열은 검색이고 어느 열은 목록인지도 열어 보기 전에는 알 수 없다. */}
        {searchable && <input ref={searchInput} type="search" value={query} placeholder={`${label} 검색`} aria-label={`${label} 검색`} data-testid={`filter-search-${key}`}
          onChange={event => setQuery(event.target.value)}
          onKeyDown={event => { if (event.key === "Escape") close(); if (event.key === "Enter") pick(key!, matches[0]?.value ?? (query.trim() || undefined)); }} />}
        {matches.length > 0
          ? <ul>{matches.slice(0, 40).map(option => <li key={option.value}>
              <button type="button" onMouseDown={event => event.preventDefault()} onClick={() => pick(key!, option.value)}>{option.label}</button>
            </li>)}</ul>
          : <p>찾는 값이 없습니다.</p>}
      </div>}
      </span>
    </th>;
  };
  /* 열 너비를 내용에 맡기면 상태 칸이 남는 공간을 전부 먹고 숫자 열끼리 붙는다. 비율을 직접
     정해 두고, 전체 탭에서만 끼어드는 "구분" 몫은 이름 열에서 덜어 낸다. */
  const actionColumn = !!selection || !!onRemoveFromBatch;
  const columns = showSource
    ? selection
      ? ["4%", "19%", "10%", "7%", "13%", "11%", "6%", "8%", "11%", "11%"]
      : actionColumn ? ["20%", "11%", "7%", "14%", "12%", "6%", "8%", "11%", "11%"]
      : ["22%", "12%", "9%", "15%", "14%", "7%", "9%", "12%"]
    : selection
      ? ["4%", "20%", "11%", "15%", "13%", "6%", "9%", "11%", "11%"]
      : actionColumn ? ["21%", "12%", "16%", "14%", "6%", "9%", "11%", "11%"]
      : ["23%", "13%", "18%", "16%", "8%", "10%", "12%"];
  /* 거르는 중에는 결과가 없어도 표를 치우지 않는다. 필터를 푸는 ✕가 머리글에 있어서,
     표가 통째로 사라지면 되돌릴 길까지 같이 사라진다. */
  const filtering = Object.values(filters?.values ?? {}).some(Boolean) || !!filters?.period;
  return <div data-testid="settlement-table">{records.length || filtering || emptyText ? <TableFrame label="정산 내역" className="payroll-ledger-scroll" tableClassName="payroll-record-table">
    <colgroup>{columns.map((width, index) => <col key={index} style={{ width }} />)}</colgroup>
    {/* 불출·회수 기록과 같은 순서로 읽는다: 누구와 무엇이 먼저, 숫자가 가운데, 상태와 시각이 끝.
       정산은 사람 단위로 끝나는 일이라 그 표와 달리 작업자가 맨 앞에 선다. */}
    <thead><tr>
      {selection && <th scope="col"><Checkbox aria-label={selection.onSelectAll ? "미정산 항목 전체 선택" : "불러온 미정산 내역 전체 선택"} disabled={disabled || !selectable.length}
        checked={selection.allSelected ?? (!!selectable.length && chosenHere.length === selectable.length)}
        indeterminate={selection.onSelectAll ? !selection.allSelected && selection.selectedIds.length > 0 : chosenHere.length > 0 && chosenHere.length < selectable.length}
        onChange={event => selection.onSelectAll ? selection.onSelectAll(event.target.checked) : selection.onSelect(event.target.checked
          ? [...new Set([...selection.selectedIds, ...selectable.map(record => record.id)])]
          : selection.selectedIds.filter(id => !selectable.some(record => record.id === id)))} /></th>}
      {/* 화면에는 실제 작업 기간을 보여 주되 정렬·기간 필터는 기존 정산 기준일을 유지한다. */}
      {head("작업 시간", { sort: "date" })}
      {head("작업자", { filter: "workerId" })}
      {showSource && head("구분")}
      {head("프로젝트", { filter: "projectName" })}
      {/* 공정 기호(S1·F2)는 붙이지 않는다. 그 기호는 목록에서 여러 공정을 나란히 놓고 고를
          때 같은 것인지 확인하라고 있는 것이고, 표는 한 줄에 하나뿐이라 도울 일이 없다.
          붙일 수 없는 줄(계약·알바 수당)도 있어 어떤 줄에만 서게 된다. */}
      {head("하위작업")}
      {/* 불출·회수·불량·잔량은 두지 않는다 — 진행 중인 일의 이야기라 작업 탭이 건별로 맡는다.
          여기 남는 수량은 돈이 계산된 그 수량 하나다 (도급은 검수를 통과한 정상 수량,
          계약은 계약 수량). 옆의 단가·정산액과 나란히 서면 무엇의 수량인지 자리가 말해 주므로
          이름은 두 글자로 둔다. */}
      {head("수량")}{head("단가")}{head("정산액(세전)", { content: <>정산액<br /><span className="payroll-pretax-heading">(세전)</span></> })}
      {/* 상태 열은 없앴다 — 정산 예정 / 정산 내역 칩이 그 축을 대신한다. 한 화면에서 같은
          구분을 두 번 말하면 어느 쪽이 지금 걸린 조건인지 매번 확인하게 된다. */}
      {/* 예정 항목은 삭제, 묶음 항목은 제외를 제공하고 완료 내역에는 동작 열을 두지 않는다. */}
      {actionColumn && <th scope="col"><span className="payroll-column-name">{onRemoveFromBatch ? "제외" : "삭제"}</span></th>}
    </tr></thead>
    <tbody>{records.map(record => <tr key={record.id} data-testid={record.status === "pending" ? "pending-settlement-record" : "settlement-row"} data-source={record.source} data-selected={selection?.selectedIds.includes(record.id) || undefined}>
      {/* 이미 묶였거나 나간 기록에는 체크박스를 그리지 않는다. 회색 체크박스는 "눌릴 것 같은데
          안 눌리는" 물건이라 매번 눌러 보게 되고, 왜 안 되는지는 칸 안에 안 적혀 있다.
          칩이 지금 보는 것이 예정인지 내역인지 말하므로 빠뜨린 것처럼 보이지도 않는다. */}
      {selection && <td data-label="선택">{record.status === "pending" && <Checkbox aria-label={`${record.workerName} ${record.title} 선택`}
        disabled={disabled}
        checked={selection.selectedIds.includes(record.id)}
        onChange={event => selection.onSelect(event.target.checked ? [...selection.selectedIds, record.id] : selection.selectedIds.filter(id => id !== record.id))} />}</td>}
      <td data-label="작업 시간"><PayrollRecordWorkTime record={record} /></td>
      <td data-label="작업자"><strong>{record.workerName}</strong>{selection && record.status === "pending" && !settlementRecordBankReady(record, people.get(record.workerId)) && <details className="payroll-bank-warning"><summary aria-label={`${record.workerName} 은행 정보 확인`}><span role="img" aria-label="정산에 필요한 은행 정보 미입력">!</span></summary><p>은행·계좌번호·예금주 정보를 작업자 관리에서 확인해 주세요.</p></details>}</td>
      {showSource && <td data-label="구분">{record.recordKind === "manual" ? "수동 정산" : sourceLabel[record.source]}</td>}
      <td data-label="프로젝트"><span className="payroll-clamp" title={record.projectName ?? undefined}>{record.projectName ?? "-"}</span></td>
      <td data-label="하위작업"><PayrollRecordTitle record={record} withProject={false} /></td>
      <td data-label="수량">{record.recordKind === "manual" ? "-" : calculationQuantity(record)}</td><td data-label="단가">{record.recordKind === "manual" ? "-" : calculationUnit(record)}</td><td data-label="정산액(세전)"><PayrollRecordAmount record={record} /></td>
      {/* 상태는 읽는 값이고 수정·삭제는 하는 일이다. 한 칸에 섞여 있던 것을 맨 끝 한 열로
          모은다. 월 수당 행은 비어 있다 — 불출·회수에서 저절로 나온 줄이라 여기서 지우면
          작업 기록과 어긋난다. */}
      {/* 별도 등록한 조정 항목과 수동 정산 항목만 삭제할 수 있다. */}
      {onRemoveFromBatch ? <td data-label="제외"><button type="button" className="payroll-batch-remove-item" disabled={disabled}
        aria-label={`${record.workerName} ${record.title} 정산 묶음에서 제외`} title="정산 묶음에서 제외"
        data-testid="remove-settlement-batch-item" onClick={() => onRemoveFromBatch(record)}>×</button></td>
      : actionColumn && <td data-label="삭제">{record.status === "pending"
        ? <div className="payroll-record-actions">
            {(record.recordKind === "adjustment" || record.recordKind === "manual" || record.source !== "piecework") && <button type="button" className="payroll-row-action" disabled={disabled} onClick={() => onDelete(record)}>삭제</button>}
          </div>
        /* 손댈 수 있는 줄에만 글이 선다. 나머지는 비워 둔다 — 머리글도 비어 있으므로 이 칸은
           "해당되면 여기에 뜬다"는 자리일 뿐, 빠진 것을 표시할 이유가 없다. */
        : null}</td>}
    </tr>)}
    {!records.length && <tr className="payroll-empty-row"><td colSpan={columns.length}>{emptyText ?? (filtering ? "거른 조건에 맞는 정산 내역이 없습니다." : "해당 조건의 정산 내역이 없습니다.")}</td></tr>}
    </tbody>
  </TableFrame> : <EmptyState>{emptyText ?? "해당 조건의 정산 내역이 없습니다."}</EmptyState>}</div>;
}
